import { useSetAtom } from "jotai";
import { useEffect } from "react";
import type { RefObject } from "react";

import { textToSpeechPlayerAtom } from "./atoms";

const SCROLL_TRIGGER_THRESHOLD_PX = 50;

export type ScrollTrigger = {
  release: () => void;
};

export type ScrollTriggerParameters = {
  onTrigger: () => void;
  /**
   * Inner scroll container to watch (e.g. the Plus article popup wrapper)
   * instead of the window. The container must be a positioned element so that
   * absolutely-positioned sentinels inside it scroll with its content.
   */
  scrollContainer?: HTMLDivElement;
};

type SentinelWatch = {
  /**
   * "exit-above": the sentinel starts inside the root; the user has moved the
   * threshold distance once the sentinel leaves the root's top edge upward.
   * "enter": the sentinel starts above the root's top edge; the user has moved
   * the threshold distance once the sentinel enters the root from above.
   */
  triggerWhen: "enter" | "exit-above";
};

const createSentinelElement = (kind: "down" | "up", positionPx: number): HTMLDivElement => {
  const element = document.createElement("div");
  element.dataset.scrollTriggerSentinel = kind;
  element.setAttribute("aria-hidden", "true");
  element.style.cssText =
    "position:absolute;top:0;left:0;width:1px;height:1px;pointer-events:none;" +
    `transform:translateY(${positionPx}px);`;
  return element;
};

/**
 * One-shot scroll-movement trigger for the text-to-speech player.
 *
 * Places invisible sentinels 50px above and below the scroll position at
 * activation time (the "anchor"). Whichever sentinel is crossed first tells
 * the direction, and the trigger fires exactly once — the collapse consumer
 * only needs a single signal per playback session, so there is no scroll-stop
 * debounce or re-arm machinery (the previous implementation's 200ms timer was
 * dead weight for this consumer).
 *
 * Uses IntersectionObserver sentinels instead of a scroll listener: zero
 * per-frame main-thread work, and the sentinels only exist while text-to-speech
 * is active, so non-listening sessions pay nothing.
 */
export const createScrollTrigger = ({
  onTrigger,
  scrollContainer,
}: ScrollTriggerParameters): ScrollTrigger => {
  if (typeof IntersectionObserver === "undefined") return { release: () => {} };

  const watches = new Map<Element, SentinelWatch>();
  const observer = new IntersectionObserver(
    entries => {
      for (const entry of entries) {
        const watch = watches.get(entry.target);
        if (!watch) continue;

        const rootTop = entry.rootBounds?.top ?? 0;
        const hasCrossedThreshold =
          watch.triggerWhen === "exit-above"
            ? !entry.isIntersecting && entry.boundingClientRect.top < rootTop
            : entry.isIntersecting;
        if (!hasCrossedThreshold) continue;

        onTrigger();
        release();
        return;
      }
    },
    { root: scrollContainer ?? null, threshold: 0 },
  );

  let isReleased = false;
  const release = (): void => {
    if (isReleased) return;
    isReleased = true;
    observer.disconnect();
    watches.forEach((_watch, element) => element.remove());
    watches.clear();
  };

  const anchor = scrollContainer ? scrollContainer.scrollTop : window.scrollY;
  const sentinelParent = scrollContainer ?? document.body;

  const downSentinel = createSentinelElement("down", anchor + SCROLL_TRIGGER_THRESHOLD_PX);
  sentinelParent.append(downSentinel);
  watches.set(downSentinel, { triggerWhen: "exit-above" });
  observer.observe(downSentinel);

  // The user cannot scroll above the scroll range's top, so an up sentinel is
  // only meaningful when the anchor sits at least the threshold below it.
  if (anchor - SCROLL_TRIGGER_THRESHOLD_PX >= 0) {
    const upSentinel = createSentinelElement("up", anchor - SCROLL_TRIGGER_THRESHOLD_PX);
    sentinelParent.append(upSentinel);
    watches.set(upSentinel, { triggerWhen: "enter" });
    observer.observe(upSentinel);
  }

  return { release };
};

export type UseScrollTriggerParameters = {
  isActive: boolean;
  /**
   * Changing this value re-arms the trigger: the previous sentinels are
   * released, the atom is reset, and a fresh anchor is taken at the current
   * scroll position. Pass the playing article's entityId so switching
   * articles mid-playback cannot carry a stale `isScrollingOverThreshold`
   * into the new article's player.
   */
  resetKey?: string;
  scrollContainerReference?: RefObject<HTMLDivElement | null>;
};

/**
 * Activates the one-shot scroll trigger while text-to-speech is playing or
 * paused, and mirrors the result into `textToSpeechPlayerAtom` — the collapse
 * consumer's only input from this module. The atom is only written on flips
 * (trigger / deactivate), never per scroll event.
 */
export const useScrollTrigger = ({
  isActive,
  resetKey,
  scrollContainerReference,
}: UseScrollTriggerParameters): void => {
  const setTextToSpeechPlayerAtom = useSetAtom(textToSpeechPlayerAtom);

  useEffect(() => {
    if (!isActive) return;
    // Never fall back to window mode when the referenced container is not
    // mounted: in the popup scenario the window does not scroll at all.
    if (scrollContainerReference && !scrollContainerReference.current) return;

    let hasTriggered = false;
    const trigger = createScrollTrigger({
      onTrigger: () => {
        hasTriggered = true;
        setTextToSpeechPlayerAtom({ isScrollingOverThreshold: true });
      },
      scrollContainer: scrollContainerReference?.current ?? undefined,
    });

    return () => {
      trigger.release();
      if (hasTriggered) {
        setTextToSpeechPlayerAtom({ isScrollingOverThreshold: false });
      }
    };
  }, [isActive, resetKey, scrollContainerReference, setTextToSpeechPlayerAtom]);
};
